/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    fork, call, put, select, take, takeEvery, all,
} from 'redux-saga/effects';
import { END } from 'redux-saga';
import mbpLogger from 'mbp-logger';

import { duck as tmDuck } from '../../../TagManager';
import memberDucks from '../../../Member/ducks';
import cartServiceClient from '../../../../../apis/cart-service-apis/cartServiceClient';
import { getAppliedGiftCards, getPassportItem } from '../Cart/Cart-Selectors';

import {
    getPassportSubscriptionStatus,
    getPassportData,
    getPassportOrderItemId,
    getIfGiftCardApplied,
    getOrderId,
    getPaymentMethod,
    getPassportCampaignData,
    getPassportABData,
    getPassportSignInData,
    getIsPassportRenewalAllowed,
} from './Passport-Selectors';

import {
    loadOrderId,
    loadPassportError,
    loadPassportLoader,
    clearPassportError,
    showPassportModalToRemoveGC,
    addPassportSubscription,
    hidePassportModalToRemoveGC,
    loadPassportDynamicCharge,
    callHandleCartFailure,
    mfeSubscribePassportBundle,
    passportPromoAppliedSuccessfully,
    addPassportByRemoveGiftCard,
    getCalculateDynamicPassport,
    mfePassportFailed,
} from './Passport-Action';

import { getBaseCode } from '../../../../../app/helpers/tracking/product/dataLayerProductHelpers';
import { setProfileUserRole } from '../../../Member/ducks/Profile/Profile-Actions';
import takeLeading from '../../../../../app/helpers/sagas/takeLeading';
import { retrieveCart } from '../Cart/ducks/RetrieveCart/RetrieveCart-Operations';
import { processFeatureErrors } from '../../../Checkout/ducks/Common/ducks/Error/Error-Operations';
import { clearOrderItemErrors } from '../../../Checkout/ducks/Common/ducks/Error/Error-Actions';
import { addToCartSuccess } from '../../../AddToCart/AddToCart-Actions';
import { getFeatureFlag } from '../App/App-Selectors';
import { prepareRetreiveCart } from '../Cart/ducks/RetrieveCart/PrepareRetreiveCart-Operations';

const {
    auth: {
        authOperations,
    },
} = memberDucks;

const { checkJWT } = authOperations.workers;

function* workerMfeLogPassportError(exception) {
    const response = exception?.response || {};
    let passportErrorMessage = 'We are sorry, there seems to be a technical issue, please try again';
    const errorMessage = response?.data?.detailedError?.errorMessage || response?.data?.errorMessage;
    const errorMessageList = response?.data?.detailedError?.validationErrors || [];
    if (errorMessage) {
        passportErrorMessage = errorMessage;
    }
    yield put(loadPassportError(passportErrorMessage, errorMessageList));
}

function* workerPassportSubscription(action) {
    const cartOrderId = yield select(getOrderId);
    yield put(loadPassportLoader(true));
    yield put(clearPassportError());
    clearOrderItemErrors(
        cartOrderId,
        'passport',
        {
            errType: 'clearAllFields',
            field: '',
        },
    );
    let trackEventCategory = '';
    const jwtToken = yield call(checkJWT);
    const passportSubscriptionStatus = yield select(getPassportSubscriptionStatus);
    const passportRenewalAllowed = yield select(getIsPassportRenewalAllowed);
    const { pageOn, history } = action.data;
    const passportData = yield select(getPassportData);
    let productCode = passportData?.item_id;
    // if passport is passed in props
    if (action.data.passportSku) {
        productCode = action.data.passportSku;
    }

    if (productCode.includes('-')) {
        const passportArray = productCode.split('-');
        productCode = (passportArray && passportArray?.[passportArray.length - 1]) || '';
    }

    const brandCode = passportData?.brand_id;
    if (!passportSubscriptionStatus && passportRenewalAllowed) {
        if (brandCode && productCode) {
            const item = { brandCode, productCode, isRenewal: true };
            const payload = {
                env: {},
                item,
                jwtToken,
            };
            const addToCartResp = yield call(cartServiceClient.addPassportToCart, payload);

            const { cartId } = addToCartResp.data;
            const orderId = [cartId];

            if (pageOn === 'product') {
                trackEventCategory = 'Shopping';
                yield put(loadOrderId(orderId));
            } else if (
                ['shipping', 'cart', 'payment', 'payment-review', 'review-order', 'review'].includes(pageOn)
            ) {
                trackEventCategory = 'Checkout';
                yield call(prepareRetreiveCart,  pageOn);
            } else if (pageOn === 'passportPage') {
                trackEventCategory = 'Passport Page';
                yield put(loadOrderId(orderId));
            } else if (pageOn === 'passportSideBar') {
                trackEventCategory = 'Passport SideBar';
                yield put(loadOrderId(orderId));
            } else if (pageOn === 'payment-floral') {
                trackEventCategory = 'Payment Floral';
                yield put(loadOrderId(orderId));
            }

            yield call(retrieveCart);
            yield put(setProfileUserRole('M'));

            const eventAction = 'Add to cart';
            const { primeName, primeSku, primePrice } = addToCartResp.data;
            const passportEventObj = {
                eventName: 'add_to_cart',
                eventCategory: trackEventCategory,
                eventAction,
                eventLabel: 'Passport|<<pageType>>',
                products: [
                    {
                        name: primeName,
                        partNumber: primeSku,
                        baseCode: getBaseCode(primeSku),
                        brand: brandCode,
                        sKUs: [{
                            partNumber: primeSku,
                        }],
                        price: primePrice,
                        quantity: '1',
                        position: 1,
                        categoryId: '12 Months of Passport',
                    },
                ],
            };

            // clicstream add to cart tracking for passport
            const passportItem = yield select(getPassportItem);

            if (Array.isArray(passportItem) && passportItem.length > 0) {
                // created separate variable for baseCode & partNumber.
                const { product, deliveryInfo, itemInfo } = passportItem[0]?.cartItems[0] || {};
                const clickstreamPayload = {
                    eventType: 'clickstream.add-cart',
                    metaData: {
                        order: {
                            orderId: cartId,
                        },
                        products: [{
                            baseCode: product?.parentProductCode,
                            name: product?.parentProductName,
                            partNumber: product?.productCode,
                            price: itemInfo?.price,
                        }],
                        zipCode: passportItem[0]?.recipientAddress?.zipCode || deliveryInfo?.zipCode || '',
                    },
                    page: {
                        title: `Checkout ${pageOn}`,
                        type: pageOn === 'cart' ? 'shoppingCart' : pageOn,
                    },
                };

                yield put(addToCartSuccess(clickstreamPayload));
            }

            yield put(tmDuck.actions.trackEvent(passportEventObj));
        } else {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-checkout',
                function: 'workerGetPassportTncContent',
                message: 'brandCode && productCode are missing',
            });

            const passportErrorMessage = 'We are sorry, there seems to be a technical issue, please try again';

            const error = {
                errorKey: null,
                errorMessage: null,
                errorCode: 400,
                fieldErrors: [
                    {
                        defaultMessage: passportErrorMessage,
                        fieldName: 'productCode',
                    },
                ],
            };
            yield fork(processFeatureErrors, {
                data: {
                    orderItemId: cartOrderId,
                    errorFeatureType: 'passport',
                    errorValues: error,
                },
            });

            yield put(loadPassportLoader(false));
            // generic error
            yield put(mfePassportFailed());
        }
    } else {
        const passportOrderItemId = yield select(getPassportOrderItemId);
        const cartId = yield select(getOrderId);

        const configObj = {
            env: {},
            jwtToken,
            orderItemId: passportOrderItemId,
            cartId,
        };

        // Remove Passport
        yield call(cartServiceClient.removeOrderItem, configObj);

        if (pageOn === 'payment') {
            trackEventCategory = 'Checkout';
            yield call(prepareRetreiveCart,  pageOn);
        }

        yield call(retrieveCart);
        yield put(setProfileUserRole(''));

        // Tealium Tracking Starts
        const eventAction = 'Remove from cart (pwa)';
        const passportEventObj = {
            eventCategory: trackEventCategory,
            eventAction,
            eventLabel: 'Passport|<<pageType>>',
        };

        yield put(tmDuck.actions.trackEvent(passportEventObj));
        // Tealium Tracking Ends
    }

    if (pageOn === 'passportPage' && history) {
        const orderId = yield select(getOrderId);
        history.push(`/checkout/cart/${orderId}`);
    }

    yield put(loadPassportLoader(false));
}

function* workerAddPassportSubscription(action) {
    const cartId = yield select(getOrderId);
    try {
        // If the page is passport that is located in precheckout Ex: /passport-flowers.
        // We need get the last cart to make sure if gift cart is in it.
        if (action.data.pageOn === 'passportPage') {
            yield call(retrieveCart);
        }

        const ifGiftCardApplied = yield select(getIfGiftCardApplied);

        if (!ifGiftCardApplied) {
            yield call(workerPassportSubscription, action);
        } else {
            // GC is applied show modal seeking to remove GC
            yield put(showPassportModalToRemoveGC());
        }
    } catch (ex) {
        yield put(callHandleCartFailure(ex));
        yield put(loadPassportLoader(false));
        yield fork(processFeatureErrors, {
            data: {
                orderItemId: cartId,
                errorFeatureType: 'passport',
                errorValues: ex.response?.data,
            },
        });
    }
}

function* watcherAddPassportSubscription() {
    yield takeEvery(addPassportSubscription().type, workerAddPassportSubscription);
}

function* workerAddPassportByRemoveGiftCard(action) {
    try {
        const cartId = yield select(getOrderId);
        const appliedGiftCards = yield select(getAppliedGiftCards);
        if (appliedGiftCards && appliedGiftCards.length > 0) {
            const jwtToken = yield call(checkJWT);

            const rmGCPromise = appliedGiftCards.map((giftCard) => {
                const { giftCardId } = giftCard;

                return cartServiceClient.removeGiftCard(
                    {
                        env: {},
                        jwtToken,
                        cartId,
                        giftCardId,
                    });
            });

            yield all(rmGCPromise);
            yield call(workerPassportSubscription, action);
            yield put(hidePassportModalToRemoveGC());
        }

        if (action.data.pageOn === 'passportPage') {
            action.data.history.push(`/checkout/cart/${cartId}`);
        }
    } catch (ex) {
        yield put(hidePassportModalToRemoveGC());
        yield put(loadPassportLoader(false));
        yield put(mfePassportFailed(ex));
    }
}

function* watcherAddPassportByRemoveGiftCard() {
    yield takeEvery(addPassportByRemoveGiftCard().type, workerAddPassportByRemoveGiftCard);
}

function* workerCalculateDynamicPassport() {
    try {
        const cartId = yield select(getOrderId);
        const isPassportEnabled = yield select(getFeatureFlag('is-passport-enabled'));
        if (isPassportEnabled) {
            if (cartId) {
                const jwtToken = yield call(checkJWT);
                const passportData = yield select(getPassportData);
                const passportABData = yield select(getPassportABData);
                const passportSignInData = yield select(getPassportSignInData);
                const passportCampaignData = yield select(getPassportCampaignData);
                const passportOrderItem = yield select(getPassportItem);
                const { isPassport, productCode } = passportOrderItem?.[0]?.cartItems?.[0]?.product || {};
                const passportBrand = passportData?.brand_id;

                // **** update passport SKU based on dynamic passport skus ****
                // default passport SKU depending on if in cart
                let passportItem = isPassport && productCode ? productCode : `${passportData?.brand_id}-${passportData?.item_id}`;
                // AB Campaign SKU
                if (passportABData?.passport_item_sku) passportItem = passportABData.passport_item_sku;
                // Passport Sign/SignedOut Logic
                if (passportSignInData?.passportEnrolmentTerm) passportItem = passportSignInData.passportEnrolmentTerm;
                // Passport UTM Campaigns
                if (passportCampaignData?.itemSku) passportItem = passportCampaignData.itemSku;

                const payload = {
                    env: {},
                    jwtToken,
                    cartId,
                    passportItem,
                    passportBrand,
                };
                const response = yield call(cartServiceClient.calculateDynamicPassport, payload);
                yield put(loadPassportDynamicCharge(response.data));
            } else { // no orderid
                const windowLocation = (typeof window !== 'undefined') ? window.location.href : '';
                mbpLogger.logError({
                    function: 'workerDynamicPassportCharge',
                    appName: process.env.npm_package_name,
                    module: 'Passport-Operations',
                    message: 'Dynamic Passport Charges - No Order Id',
                    cartId,
                    windowLocation,
                });
            }
        }
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'workerDynamicPassportCharge',
            jsError: ex,
            message: 'Load Passport Dynamic Charge',
        });
    }
}

function* workerMfeSubscribePassportBundle(action) {
    const { passportBundle, history } = action.data;

    yield put(tmDuck.actions.trackEvent({
        eventCategory: 'product',
        eventAction: 'Passport Banner Click',
        eventLabel: 'Bundle',
    }));

    yield put(addPassportSubscription('product', passportBundle?.passportSku));

    // regext for -- catching promocode from the url params
    if (history?.location?.search && new RegExp(/promoCode=(.*)/).test(history.location.search)) {
        const match = (history.location.search).match(/promoCode=(.*)/);
        if (match.length >= 1) {
            const promotionCode = match[1];
            const jwtToken = yield call(checkJWT);
            const selectedPaymentMethod = yield select(getPaymentMethod);
            const billingZipCode = selectedPaymentMethod && selectedPaymentMethod.id ? selectedPaymentMethod.id : '';
            const cartId = yield select(getOrderId);
            const payload = {
                env: {},
                jwtToken,
                cartId,
                promotionCode,
                membershipCode: '',
                billingZipCode,
            };

            // Apply Promo
            yield call(cartServiceClient.applyPromotion, payload);

            yield put(passportPromoAppliedSuccessfully());
        }
    }
}

function* watcherMfeSubscribePassportBundle() {
    yield takeEvery(mfeSubscribePassportBundle().type, workerMfeSubscribePassportBundle);
}

function* watchCalculateDynamicPassport() {
    yield takeLeading(getCalculateDynamicPassport().type, workerCalculateDynamicPassport);
}

function* watchMfePassportFailure() {
    let action = yield take(mfePassportFailed().type);
    while (action !== END) {
        yield fork(workerMfeLogPassportError, action.payload);
        action = yield take(mfePassportFailed().type);
    }
}

const watchers = [
    fork(watcherAddPassportByRemoveGiftCard),
    fork(watcherAddPassportSubscription),
    fork(watchCalculateDynamicPassport),
    fork(watchMfePassportFailure),
    fork(watcherMfeSubscribePassportBundle),
];

export {
    watchers,
};

export default {};
